import { Slider, Typography } from '@mui/material';

/**
 * Slider styling shared by every contouring tool tray. Extracted so a
 * fifth tray cannot introduce a fifth spelling of the same control.
 */
const sliderSx = {
    height: 14,
    '& .MuiSlider-rail': {
        height: 14,
        borderRadius: 100,
        opacity: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& .MuiSlider-track': {
        height: 14,
        borderRadius: 100,
        border: 'none',
        backgroundColor: 'primary.main',
    },
    '& .MuiSlider-thumb': {
        height: 12,
        width: 12,
        backgroundColor: '#fff',
        border: 'none',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
        zIndex: 1,
        '&:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
        },
    },
};

interface ToolTraySliderProps {
    /** Caption to the left of the slider; omitted where the tray has room for none. */
    label?: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    /**
     * Read-out to the right of the slider, formatted BY THE CALLER
     * including its unit — the tray is a UI-boundary control, so the
     * conversion and precision belong with the value's owner, not here.
     * Omitted for a slider whose setting has no meaningful number to
     * show (see the smoothing brush's strength).
     */
    valueText?: string;
    /** Width reserved for {@link valueText}, so it cannot shift the row as it changes. */
    valueMinWidth?: number;
    sliderMinWidth?: number;
    /**
     * Thumb offset for a slider whose travel starts below its usable
     * range (the brush's negative minimum), where the default centring
     * would let the thumb overhang the rail.
     */
    thumbMarginLeft?: string;
    'data-testid'?: string;
}

/**
 * One labelled slider in a contouring tool tray: caption, slider, and
 * read-out. Returns a fragment rather than its own container so the
 * tray's flex row spaces every element alike (see
 * {@link ./toolTraySx.toolTrayRowSx}).
 */
export default function ToolTraySlider({
    label,
    value,
    onChange,
    min,
    max,
    step,
    valueText,
    valueMinWidth = 38,
    sliderMinWidth = 100,
    thumbMarginLeft,
    'data-testid': dataTestId,
}: ToolTraySliderProps) {
    return (
        <>
            {label !== undefined && (
                <Typography
                    variant="body2"
                    fontSize={11}
                    color="rgba(255, 255, 255, 0.6)"
                    sx={{ flexShrink: 0 }}
                >
                    {label}
                </Typography>
            )}
            <Slider
                value={value}
                onChange={(_, newValue) =>
                    onChange(Array.isArray(newValue) ? newValue[0] : newValue)
                }
                step={step}
                min={min}
                max={max}
                size="small"
                sx={{
                    ...sliderSx,
                    minWidth: sliderMinWidth,
                    ...(thumbMarginLeft !== undefined && {
                        '& .MuiSlider-thumb': {
                            ...sliderSx['& .MuiSlider-thumb'],
                            marginLeft: thumbMarginLeft,
                        },
                    }),
                }}
                data-testid={dataTestId}
            />
            {valueText !== undefined && (
                <Typography
                    variant="body2"
                    fontSize={11}
                    color="rgba(255, 255, 255, 0.85)"
                    sx={{
                        flexShrink: 0,
                        minWidth: valueMinWidth,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {valueText}
                </Typography>
            )}
        </>
    );
}
